import { useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { ProductModelInstance } from 'src/store/_models/product';
import {
  DYNAMIC_MODAL_VIEWS,
  MODAL_QUALITY_OPEN_POINTS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { LookupCodes } from 'src/constants/lookup.constants';
import { ProductOptionChoiceInstance } from 'src/store/_models/productOptionChoice';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import amplitude from 'src/_shared/amplitude';
import { CtasType } from '@photosi/rn-components/modals/dynamic-modals/modal-quality-alert/ModalQualityAlert.constants';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import { DYNAMIC_MODAL_GROUPS } from 'src/store/toast/toastStore.types';
import { getToolOptionTitleI18nKey } from 'src/_shared/utils/getI18nKeys';
import { parseAnalyticsParamsByUserImage } from 'src/_shared/analytics.utils';
import { logError } from 'src/_shared/logger';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

/**
 * useGridItemContentHeaderLogic
 *
 * Handles hover state for the action icons in GridItemContentHeader.
 * The actual fade animations are handled by FadeIn/FadeOut in the component.
 */
export const useGridItemContentHeaderLogicV3 = (product: ProductModelInstance) => {
  const {
    i18n,
    dynamicModal: { openModal },
    project: { isProductEditModeActive, isProjectAddedToCart, activeProject },
    toastStore: { showOnDuplicateToast, showToast },
  } = useStore();
  const navigation = useTypedNavigation<ROUTE_NAMES.EditorNew>();
  const route = useTypedRoute<ROUTE_NAMES.EditorNew>();
  const currentParams = route?.params;
  const [productLookup, setProductLookup] = useState(LookupCodes.FORMAT);
  const { isMobile } = useResponsiveScreen();

  const isMultiphotoProductComplete =
    product.preview.isMultiphoto &&
    !isProductEditModeActive &&
    product.preview.userImageAreas.filter(e => e.userImage === undefined).length === 0;

  const onPressEdit = async () => {
    const userImage = product.userImages[0];
    navigation.navigate(ROUTE_NAMES.Edit, {
      productId: product.id,
      ...currentParams,
    });
    amplitude.trackEvent(AnalyticsEvents.personalize.editImageClick, {
      ...parseAnalyticsParamsByUserImage(userImage),
      source: 'options_menu',
      section_name: SECTION_NAMES.OPTIONS_MENU,
    });
  };

  const onPressQuality = (options = false) => {
    const userImage = product.userImagesWithQualityAlert[0];
    openModal({
      qualityAlertUserImage: userImage,
      modalQualitySuggestions: userImage.qualitySuggestions,
      modalQualityCtas: CtasType.CLOSE,
      modalQualityOpenPoint: MODAL_QUALITY_OPEN_POINTS.photo,
      dynamicModalView: DYNAMIC_MODAL_VIEWS.QualityAlert,
      actionCallback: () => {},
    });
    amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertClick, {
      section_name: options ? SECTION_NAMES.OPTIONS_MENU : SECTION_NAMES.PERSONALIZE,
    });
  };

  const [isCheckboxHovered, setIsCheckboxHovered] = useState(false);

  // Mouse enter/leave handlers for checkbox icon
  const handleCheckboxMouseEnter = () => {
    setIsCheckboxHovered(true);
  };

  const handleCheckboxMouseLeave = () => {
    setIsCheckboxHovered(false);
  };

  const syncProductsOptions = () => {
    product.syncProductsOptions();
  };

  const getProductOptions = (type: LookupCodes) => {
    return product.lookupOptions?.find(item => item.code === type)?.choices;
  };

  const getPaperOptions = () => {
    syncProductsOptions();
    return getProductOptions(LookupCodes.PAPER);
  };

  const applyLookupToProductGroup = (item: ProductOptionChoiceInstance) => {
    activeProject?.applyLookupToProductGroup(
      [product],
      item.productConfigurationIdsMap,
      item.referencingOtherGroupCode,
    );
    let eventName, eventData;
    if (productLookup === LookupCodes.FORMAT) {
      eventName = AnalyticsEvents.personalize.editFormatClick;
      eventData = {
        format: item.productOptionChoiceCode,
        section_name: SECTION_NAMES.OPTIONS_MENU,
      };
    } else {
      eventName = AnalyticsEvents.personalize.photoPaperClick;
      eventData = {
        paper: item.productOptionChoiceCode,
      };
    }
    amplitude.trackEvent(eventName, eventData);
    showToast({
      message: i18n.t('toast:product-option-applied-successfully', {
        option: i18n.t(getToolOptionTitleI18nKey(item.productOptionCode)),
      }),
      duration: 3000,
      id: `apply-product-option-${item.productOptionChoiceCode}`,
      group: DYNAMIC_MODAL_GROUPS.ApplyProductOption,
    });
  };

  const openLookup = () => {
    product.setIsSelected(true);
  };

  const getFormatOptions = () => {
    syncProductsOptions();
    return getProductOptions(LookupCodes.FORMAT);
  };

  const onDuplicate = () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.duplicatePhotoClick, {
      section_name: SECTION_NAMES.OPTIONS_MENU,
    });
    product
      .duplicate()
      .then(() => {
        showOnDuplicateToast({
          message: i18n.t('toast:duplicate'),
          id: 'toast-duplicate',
        });
      })
      .catch(e => {
        logError('Error duplicating product', e);
      });
  };

  const onPressEditPersonalize = async () => {
    const userImage = product.userImages[0];
    navigation.navigate(ROUTE_NAMES.Edit, {
      productId: product.id,
      ...currentParams,
    });
    amplitude.trackEvent(AnalyticsEvents.personalize.editImageClick, {
      ...parseAnalyticsParamsByUserImage(userImage),
      source: 'personalize',
      section_name: SECTION_NAMES.PERSONALIZE,
    });
  };

  const avoidWidthImage = isMobile && activeProject?.gridColumns === 2;

  return {
    i18n,
    isCheckboxHovered,
    handleCheckboxMouseEnter,
    handleCheckboxMouseLeave,
    onPressEdit,
    isMultiphotoProductComplete,
    isProjectAddedToCart,
    syncProductsOptions,
    getPaperOptions,
    applyLookupToProductGroup,
    openLookup,
    productLookup,
    setProductLookup,
    onPressQuality,
    onDuplicate,
    getFormatOptions,
    onPressEditPersonalize,
    gridColumns: activeProject?.gridColumns,
    avoidWidthImage,
  };
};
